// A time field in a form, in place of `<input type="time">`: with a mouse it is
// a button showing the time, which opens the time picker (time-picker.tsx); on
// a touch screen it stays the native field (the phone's own picker).
//
// Promise: the value is "HH:MM"; choosing an hour or a minute calls
// `onChange` with the new time (the panel stays open over an hour, as the
// native field changes on each part). The time shows as `9:30`.
import { useRef, useState } from "react";
import { useCoarsePointer } from "./pointer";
import { parseTime, TimePopover } from "./time-picker";

export interface TimeFieldProps {
  /** "HH:MM". */
  value: string;
  onChange(value: string): void;
  /** Read aloud for the field and its panel. */
  label: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  /** IANA time zone for "Set to now" (the account's). */
  timeZone?: string;
  /** The field's look (the button and the native field alike). */
  className?: string;
}

export function TimeField({
  value,
  onChange,
  label,
  min,
  max,
  disabled,
  timeZone,
  className = "",
}: TimeFieldProps) {
  const coarse = useCoarsePointer();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (coarse)
    return (
      <input
        type="time"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        className={`appearance-none [&::-webkit-date-and-time-value]:min-h-[1lh] [&::-webkit-date-and-time-value]:text-left ${className}`}
      />
    );
  const t = parseTime(value);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`cursor-pointer text-left tabular-nums disabled:cursor-default ${className}`}
      >
        {t ? `${t.h}:${String(t.m).padStart(2, "0")}` : " "}
      </button>
      <TimePopover
        anchor={anchor}
        open={open}
        onClose={() => setOpen(false)}
        label={label}
        value={value}
        min={min}
        max={max}
        timeZone={timeZone}
        onChange={(v) => v && onChange(v)}
      />
    </>
  );
}
